/**
 * <predictive-search> — Shopify's native Predictive Search API
 * (`/search/suggest.json`), grouped results shape (products / collections /
 * pages / articles, each capped) adapted from
 * ColoredByte/coloredvision-store's predictive search implementation (see
 * docs/REFERENCE_AUDIT.md).
 */
class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('[data-predictive-search-input]');
    this.resultsEl = this.querySelector('[data-predictive-search-results]');
    this.abortController = null;
    this.cache = new Map();

    if (this.input) {
      this.input.addEventListener('input', window.NantesUtils.debounce(this.onInput.bind(this), 250));
    }
  }

  connectedCallback() {
    document.querySelectorAll('[data-search-toggle]').forEach((toggle) =>
      toggle.addEventListener('click', () => this.toggle())
    );
  }

  toggle() {
    this.hidden = !this.hidden;
    document.querySelectorAll('[data-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', String(!this.hidden)));
    if (!this.hidden) requestAnimationFrame(() => this.input && this.input.focus());
  }

  async onInput(event) {
    const term = event.target.value.trim();
    if (term.length < 2) {
      this.resultsEl.innerHTML = '';
      return;
    }

    if (this.cache.has(term)) {
      this.render(this.cache.get(term), term);
      return;
    }

    this.resultsEl.innerHTML = `<p class="predictive-search__loading">${window.NantesStrings.searchLoading}</p>`;

    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product,collection,page,article&resources[limit]=4&resources[options][unavailable_products]=last`,
        { signal: this.abortController.signal }
      );
      const data = await response.json();
      this.cache.set(term, data.resources.results);
      this.render(data.resources.results, term);
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.resultsEl.innerHTML = `<p class="predictive-search__empty">${window.NantesStrings.searchError}</p>`;
      }
    }
  }

  render(results, term) {
    const groups = [
      { key: 'products', label: window.NantesStrings.searchProducts },
      { key: 'collections', label: window.NantesStrings.searchCollections },
      { key: 'pages', label: window.NantesStrings.searchPages },
      { key: 'articles', label: window.NantesStrings.searchArticles },
    ];

    const nonEmpty = groups.filter((g) => results[g.key] && results[g.key].length);

    if (!nonEmpty.length) {
      this.resultsEl.innerHTML = `<p class="predictive-search__empty">${window.NantesStrings.searchNoResults.replace('{{ terms }}', term)}</p>`;
      return;
    }

    const html = nonEmpty
      .map(
        (group) => `
      <div class="predictive-search__group">
        <p class="predictive-search__group-title">${group.label}</p>
        ${results[group.key]
          .map(
            (item) => `
          <a href="${item.url}" class="predictive-search__result">
            ${
              item.image
                ? `<span class="predictive-search__result-media"><img src="${item.image}" alt="" loading="lazy" width="48" height="48"></span>`
                : ''
            }
            <span>
              <span class="predictive-search__result-title">${item.title}</span>
              ${item.price ? `<span class="predictive-search__result-price">${item.price}</span>` : ''}
            </span>
          </a>`
          )
          .join('')}
      </div>`
      )
      .join('');

    this.resultsEl.innerHTML =
      html +
      `<a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(term)}" class="predictive-search__view-all">${window.NantesStrings.searchViewAll}</a>`;
  }
}

customElements.define('predictive-search', PredictiveSearch);
